import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, signal, computed, ViewChild, OnDestroy, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

// Services
import { WorkoutInfoService } from '@foxera/feature/clients/services/workout-info.service';
import { BranchService } from '@foxera/feature/settings/services/branch.service';
import { ResponsiveService } from '@foxera/shared/services/responsive.service';

// Components
import { ExerciseFilterDialogComponent } from '@foxera/feature/clients/components/exercise-filter-dialog/exercise-filter-dialog.component';
import { GlobalLoadingComponent } from '@foxera/shared/components/foxera-loading/foxera-loading.component';
import { ClientViewExerciseComponent } from '@foxera/shared/components/client-view-exercise/client-view-exercise.component';
import { FoxeraDrawerFullComponent } from '@foxera/shared/components/foxera-drawer-full/foxera-drawer-full.component';
import { FoxeraBranchSelectorComponent } from '@foxera/shared/components/foxera-branch-selector/foxera-branch-selector.component';

// Interfaces
import { IExerciseSearchItem, IExerciseSearchResponse, IExerciseFilterConstants } from '@foxera/shared/interfaces/client.interface';
import { IExerciseFilters } from '@foxera/feature/clients/interfaces/filter-exercice.interface';
import { IBranch } from '@foxera/shared/interfaces/branches.interface';

@Component({
  selector: 'app-client-search-exercises',
  standalone: true,
  templateUrl: './client-search-exercises.component.html',
  styleUrls: ['./client-search-exercises.component.css'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    ExerciseFilterDialogComponent,
    GlobalLoadingComponent,
    ClientViewExerciseComponent,
    FoxeraDrawerFullComponent,
    FoxeraBranchSelectorComponent,
  ]
})
export class ClientSearchExercisesComponent implements OnInit, OnDestroy {
  readonly responsiveService = inject(ResponsiveService);
  
  visible = signal<boolean>(true);
  @Input() mode: 'add' | 'switch' | 'browse' = 'add'; // add exercise, switch exercise, or browse only
  @Input() sectionTitle?: string; // section where exercise will be added/switched
  @Input() clientId?: number; // client ID to send with the exercise search request
  @Input() initialMovementPatterns?: string[]; // initial movement patterns to filter by (for switch mode)
  @Input() lockedBranchId?: string; // when set, branch selector is read-only (e.g. from client-plan)
  @Output() close = new EventEmitter<void>();
  @Output() exercisesSelected = new EventEmitter<IExerciseSearchItem[]>(); // Changed to emit array

  @ViewChild('gridContainer', { read: ElementRef }) gridContainer!: ElementRef;

  // Search & Filter
  searchControl = new FormControl('');
  branchFilterControl = new FormControl<string | null>(null);
  readonly selectedBranch = signal<IBranch | null>(null);
  
  // Filter dialog
  isFilterDialogVisible = signal<boolean>(false);
  filterConstants = signal<IExerciseFilterConstants | null>(null);
  currentFilters = signal<IExerciseFilters>({
    targetedMuscles: [],
    categories: [],
    levels: [],
    muscleFilterMode: 'AtLeast',
    mainEquipment: null,
    supportEquipment: null,
    exerciseAccess: null,
    phases: [],
    movementPatterns: []
  });
  
  // Hide Type filter when section title is set (phase is predetermined)
  hideTypeFilter = computed(() => !!this.sectionTitle);

  // Exercise selection & view
  selectedExercisesForApply = signal<IExerciseSearchItem[]>([]); // Changed to array for multi-select
  exerciseToView = signal<IExerciseSearchItem | null>(null);
  isViewDialogVisible = signal<boolean>(false);
  
  // Initial filter tracking
  hasInitialFilter = signal<boolean>(false);
  initialFilterMovementPatterns = signal<string[]>([]);
  
  // Branch ID
  private branchId: string | null = null;
  
  // Exercise data
  exercises = signal<IExerciseSearchItem[]>([]);
  totalCount = signal<number>(0);
  isLoading = signal<boolean>(false);
  isLoadingMore = signal<boolean>(false);
  
  // Pagination
  private readonly PAGE_SIZE = 20;
  private currentSkip = 0;
  private isScrollLoading = false;
  private scrollListener: ((event: Event) => void) | null = null;

  // Skeleton rows for loading state
  skeletonCount = signal<number>(0);

  // Computed signals
  activeFilterCount = computed(() => {
    const filters = this.currentFilters();
    let count = 0;
    count += filters.targetedMuscles.length;
    count += filters.categories.length;
    count += filters.levels.length;
    count += filters.movementPatterns.length;
    if (filters.mainEquipment) count++;
    if (filters.supportEquipment) count++;
    if (filters.exerciseAccess) count++;
    count += filters.phases.length;
    return count;
  });

  // Get all active filters as display items
  activeFilterTags = computed<string[]>(() => {
    const filters = this.currentFilters();
    const tags: string[] = [];

    filters.movementPatterns.forEach(pattern => tags.push(pattern));
    
    // Add phase filters
    const phaseDisplayMap: Record<string, string> = {
      'WarmUp': 'Warm Up',
      'ResistanceTraining': 'Resistance Training',
      'Cardio': 'Cardio',
      'CoolDown': 'Cool Down'
    };
    filters.phases.forEach(phase => {
      tags.push(phaseDisplayMap[phase] || phase);
    });
    
    // Add muscle filters
    filters.targetedMuscles.forEach(muscle => tags.push(muscle));
    
    // Add category filters
    filters.categories.forEach(category => tags.push(category));
    
    // Add level filters
    filters.levels.forEach(level => tags.push(level));
    
    // Add equipment filters
    if (filters.mainEquipment) tags.push(filters.mainEquipment);
    if (filters.supportEquipment) tags.push(filters.supportEquipment);
    if (filters.exerciseAccess) tags.push(filters.exerciseAccess);
    
    return tags;
  });

  canLoadMore = computed(() => {
    return this.exercises().length < this.totalCount() && !this.isLoadingMore();
  });

  skeletonArray = computed(() => {
    return Array.from({ length: this.skeletonCount() }, (_, i) => i);
  });

  headerTitle = computed(() => {
    if (this.mode === 'browse') {
      return 'Browse Exercises';
    }
    if (this.mode === 'switch') {
      return `Switch Exercise${this.sectionTitle ? ` in ${this.sectionTitle}` : ''}`;
    }
    return `Add Exercise${this.sectionTitle ? ` to ${this.sectionTitle}` : ''}`;
  });

  showSelectionUI = computed(() => {
    return this.mode === 'add' || this.mode === 'switch';
  });

  isBranchLocked = computed(() => !!this.lockedBranchId);

  constructor(
    private workoutInfoService: WorkoutInfoService,
    private branchService: BranchService
  ) {}

  /**
   * Map section title to phase enum value
   * Matches backend PhaseTitle enum: WarmUp, ResistanceTraining, Cardio, CoolDown
   */
  private mapSectionTitleToPhase(sectionTitle?: string): 'WarmUp' | 'ResistanceTraining' | 'Cardio' | 'CoolDown' | undefined {
    if (!sectionTitle) return undefined;
    
    const normalized = sectionTitle.toLowerCase().replace(/[\s-]/g, '');
    
    const phaseMap: Record<string, 'WarmUp' | 'ResistanceTraining' | 'Cardio' | 'CoolDown'> = {
      'warmup': 'WarmUp',
      'resistancetraining': 'ResistanceTraining',
      'cardio': 'Cardio',
      'cooldown': 'CoolDown'
    };
    
    return phaseMap[normalized];
  }

  ngOnInit(): void {
    // Set initial movement pattern filters if provided (for switch mode)
    // Skip initial filter for Cardio exercises
    const phase = this.mapSectionTitleToPhase(this.sectionTitle);
    const isCardio = phase === 'Cardio';
    
    if (this.initialMovementPatterns && this.initialMovementPatterns.length > 0 && !isCardio) {
      this.currentFilters.set({
        ...this.currentFilters(),
        movementPatterns: this.initialMovementPatterns
      });
      this.hasInitialFilter.set(true);
      this.initialFilterMovementPatterns.set(this.initialMovementPatterns);
    }

    // Branch selector will auto-select the first branch and trigger loading
    if (this.lockedBranchId) {
      this.initializeLockedBranch(this.lockedBranchId);
    }

    // Setup search debounce
    this.searchControl.valueChanges
      .pipe(
        debounceTime(400),
        distinctUntilChanged()
      )
      .subscribe(() => {
        this.resetAndLoad();
      });

    // Attach scroll listener after a delay to ensure DOM is ready
    setTimeout(() => {
      this.attachScrollListener();
    }, 300);
  }

  ngOnDestroy(): void {
    // Clean up scroll listener on component destroy
    this.removeScrollListener();
  }

  /**
   * Handle branch selection from branch selector
   */
  onBranchSelected(event: { branch: IBranch | null; branchName: string | null }): void {
    if (this.isBranchLocked()) {
      return;
    }

    const { branch, branchName } = event;

    this.branchFilterControl.setValue(branchName);
    this.selectedBranch.set(branch);

    if (branch?.id) {
      this.branchId = branch.id;
      this.loadFilterConstants();
      this.resetAndLoad();
    } else {
      this.branchId = null;
      this.exercises.set([]);
      this.totalCount.set(0);
      this.filterConstants.set(null);
      this.isLoading.set(false);
    }
  }

  private initializeLockedBranch(branchId: string): void {
    this.branchService.getBranches().subscribe({
      next: (branches) => {
        const branch = branches.find((item) => item.id === branchId) ?? null;
        if (!branch) {
          return;
        }

        this.branchFilterControl.setValue(branch.name, { emitEvent: false });
        this.selectedBranch.set(branch);
        this.branchId = branch.id;
        this.loadFilterConstants();
        this.resetAndLoad();
      }
    });
  }

  /**
   * Load filter constants from API using the current branchId
   */
  private loadFilterConstants(): void {
    if (!this.branchId) {
      return;
    }

    const phase = this.mapSectionTitleToPhase(this.sectionTitle);
    this.workoutInfoService.getExerciseFilterConstants(this.branchId, phase).subscribe({
      next: (constants) => {
        this.filterConstants.set(constants);
      },
      error: () => {
        // Handle error - unable to fetch filter constants
      }
    });
  }

  /**
   * Attach scroll listener to grid container
   */
  private attachScrollListener(): void {
    const scrollContainer = this.getScrollContainer();
    if (scrollContainer && !this.scrollListener) {
      // Create and store the bound function
      this.scrollListener = this.onScroll.bind(this);
      scrollContainer.addEventListener('scroll', this.scrollListener);
    }
  }

  /**
   * Remove scroll listener from grid container
   */
  private removeScrollListener(): void {
    const scrollContainer = this.getScrollContainer();
    if (scrollContainer && this.scrollListener) {
      scrollContainer.removeEventListener('scroll', this.scrollListener);
      this.scrollListener = null;
    }
  }

  /**
   * Get the scroll container element
   */
  private getScrollContainer(): HTMLElement | null {
    if (!this.gridContainer) return null;
    
    // The scroll container is the flex-1 overflow-y-auto div
    const containers = this.gridContainer.nativeElement.querySelectorAll('.overflow-y-auto');
    return containers[0] as HTMLElement || null;
  }

  /**
   * Forward template scroll events to the shared lazy-load logic.
   */
  onContentScroll(event: Event): void {
    this.onScroll(event);
  }

  /**
   * Handle scroll event for infinite loading
   */
  private onScroll(event: Event): void {
    const target = event.target as HTMLElement;
    
    // Prevent multiple simultaneous load requests
    if (this.isScrollLoading || this.isLoadingMore()) {
      return;
    }

    // Check if there's more data to load
    if (this.exercises().length >= this.totalCount()) {
      return;
    }

    // Check if currently loading initial data
    if (this.isLoading()) {
      return;
    }

    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;

    // Safety check
    if (!scrollHeight || scrollHeight === 0 || !clientHeight || clientHeight === 0) {
      return;
    }

    // Calculate if we're near the bottom (within 200px threshold)
    const threshold = 200;
    const scrollBottom = scrollTop + clientHeight;
    const distanceFromBottom = scrollHeight - scrollBottom;
    const isNearBottom = distanceFromBottom <= threshold;

    if (isNearBottom) {
      this.isScrollLoading = true;
      this.onLoadMore();
      
      // Reset loading flag after a short delay
      setTimeout(() => {
        this.isScrollLoading = false;
      }, 1000);
    }
  }

  /**
   * Load exercises with current filters
   */
  private loadExercises(append: boolean = false): void {
    // Don't load exercises if branchId is not set yet
    if (!this.branchId) {
      return;
    }

    if (append) {
      this.isLoadingMore.set(true);
      this.skeletonCount.set(4); // Show 4 skeleton cards
    } else {
      this.isLoading.set(true);
      this.exercises.set([]);
      this.skeletonCount.set(0);
    }

    const filters = this.currentFilters();
    const sectionPhase = this.mapSectionTitleToPhase(this.sectionTitle);
    const filterPhases = filters.phases || [];
    
    // Combine section phase with filter phases and deduplicate
    const combinedPhases = sectionPhase 
      ? [...filterPhases, sectionPhase].filter((v, i, a) => a.indexOf(v) === i)
      : filterPhases;
    
    const params = {
      skip: this.currentSkip,
      top: this.PAGE_SIZE,
      name: this.searchControl.value || undefined,
      categories: filters.categories.length > 0 ? filters.categories : undefined,
      muscles: filters.targetedMuscles.length > 0 ? filters.targetedMuscles : undefined,
      muscleFilterMode: filters.muscleFilterMode,
      mainEquipment: filters.mainEquipment || undefined,
      supportEquipment: filters.supportEquipment || undefined,
      levels: filters.levels.length > 0 ? filters.levels : undefined,
      phases: combinedPhases.length > 0 ? combinedPhases : undefined,
      clientId: this.clientId,
      exerciseAccess: filters.exerciseAccess || undefined,
      movementPatterns: filters.movementPatterns.length > 0 ? filters.movementPatterns : undefined
    };

    this.workoutInfoService.searchExercises(this.branchId, params).subscribe({
      next: (response: IExerciseSearchResponse) => {
        if (append) {
          this.exercises.set([...this.exercises(), ...response.exercises]);
        } else {
          this.exercises.set(response.exercises);
        }
        this.totalCount.set(response.totalCount);
        this.isLoading.set(false);
        this.isLoadingMore.set(false);
        this.skeletonCount.set(0);
      },
      error: (error) => {
        this.isLoading.set(false);
        this.isLoadingMore.set(false);
        this.skeletonCount.set(0);
      }
    });
  }

  /**
   * Reset pagination and reload
   */
  private resetAndLoad(): void {
    this.currentSkip = 0;
    this.isScrollLoading = false;
    this.loadExercises(false);
  }

  /**
   * Load more exercises (lazy loading)
   */
  onLoadMore(): void {
    if (this.exercises().length >= this.totalCount()) {
      return; // All loaded
    }
    this.currentSkip += this.PAGE_SIZE;
    this.loadExercises(true);
  }

  /**
   * Open filter dialog
   */
  onOpenFilters(): void {
    this.isFilterDialogVisible.set(true);
  }

  /**
   * Close filter dialog
   */
  onCloseFilterDialog(): void {
    this.isFilterDialogVisible.set(false);
  }

  /**
   * Apply filters from dialog
   */
  onApplyFilters(filters: IExerciseFilters): void {
    this.currentFilters.set(filters);
    this.isFilterDialogVisible.set(false);
    // Mark as having filters (beyond initial)
    if (this.activeFilterCount() > 0) {
      this.hasInitialFilter.set(true);
    }
    this.resetAndLoad();
  }

  /**
   * Clear all filters including initial filter
   */
  onClearAllFilters(): void {
    this.currentFilters.set({
      targetedMuscles: [],
      categories: [],
      levels: [],
      muscleFilterMode: 'AtLeast',
      mainEquipment: null,
      supportEquipment: null,
      exerciseAccess: null,
      phases: [],
      movementPatterns: []
    });
    this.hasInitialFilter.set(false);
    this.initialFilterMovementPatterns.set([]);
    this.resetAndLoad();
  }

  /**
   * Toggle exercise selection (checkbox)
   * - Add mode: multi-select
   * - Switch mode: single-select
   */
  onToggleExerciseSelection(exercise: IExerciseSearchItem): void {
    const currentSelected = this.selectedExercisesForApply();
    
    if (this.mode === 'switch') {
      // Switch mode: single selection
      if (currentSelected.length > 0 && currentSelected[0].id === exercise.id) {
        this.selectedExercisesForApply.set([]);
      } else {
        this.selectedExercisesForApply.set([exercise]);
      }
    } else {
      // Add mode: multi-select
      const index = currentSelected.findIndex(ex => ex.id === exercise.id);
      if (index > -1) {
        // Deselect
        const newSelected = currentSelected.filter((_, i) => i !== index);
        this.selectedExercisesForApply.set(newSelected);
      } else {
        // Select
        this.selectedExercisesForApply.set([...currentSelected, exercise]);
      }
    }
  }

  /**
   * Check if exercise is selected
   */
  isExerciseSelected(exercise: IExerciseSearchItem): boolean {
    return this.selectedExercisesForApply().some(ex => ex.id === exercise.id);
  }

  /**
   * Open exercise detail view
   */
  onViewExercise(exercise: IExerciseSearchItem, event: Event): void {
    event.stopPropagation(); // Prevent checkbox toggle
    this.exerciseToView.set(exercise);
    this.isViewDialogVisible.set(true);
  }

  /**
   * Close exercise view dialog
   */
  onCloseViewDialog(): void {
    this.isViewDialogVisible.set(false);
    this.exerciseToView.set(null);
  }

  /**
   * Handle Vixey availability change - reload exercises
   */
  onVixeyAvailabilityChanged(): void {
    // Reload the exercise list to reflect the changes
    this.resetAndLoad();
  }

  /**
   * Apply the selected exercise(s)
   */
  onApplySelection(): void {
    const selected = this.selectedExercisesForApply();
    if (selected.length > 0) {
      this.exercisesSelected.emit(selected);
      this.onClose();
    }
  }

  /**
   * Close the search dialog
   */
  onClose(): void {
    // Clean up scroll listener
    this.removeScrollListener();
    
    // For mobile, set visible to false and let the drawer handle the animation
    if (this.responsiveService.isMobileView()) {
      this.visible.set(false);
        this.close.emit();
    } else {
      this.close.emit();
    }
  }

  /**
   * Get level badge class
   */
  getLevelClass(level: string): string {
    const classes: Record<string, string> = {
      'Beginner': 'border border-green-100 bg-green-50 text-green-950',
      'Intermediate': 'border border-orange-200 bg-orange-100 text-orange-950',
      'Advanced': 'border border-red-100 bg-red-50 text-red-950'
    };
    return classes[level] || 'border border-grey-100 bg-grey-50 text-grey-900';
  }
}

